import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFixedFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/button";
import { cn } from "utils/cn";
import { toast } from "@/utils/toast";
import { useAddTenantMembers } from "@/hooks/useAddTenantMembers";

const SEPARATOR_REGEX = /[,\s]+/;

const MAX_EMAILS_IN_WARNING = 5;

function splitAndDedupe(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const token of raw.split(SEPARATOR_REGEX)) {
    const normalized = token.trim().toLowerCase();
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
      out.push(normalized);
    }
  }
  return out;
}

const perEmailSchema = yup.string().email();

function buildSchema(
  t: (key: string, vars?: Record<string, unknown>) => string,
) {
  return yup.object({
    emails: yup
      .string()
      .defined()
      .test("non-empty", (value, ctx) => {
        const tokens = splitAndDedupe(value ?? "");
        if (tokens.length === 0) {
          return ctx.createError({
            message: t("admin:tenants.detail.addMembersDialog.errorEmptyInput"),
          });
        }
        const invalid = tokens.filter(
          (tok) => !perEmailSchema.isValidSync(tok),
        );
        if (invalid.length > 0) {
          return ctx.createError({
            message: t(
              "admin:tenants.detail.addMembersDialog.errorInvalidEmails",
              { emails: invalid.join(", ") },
            ),
          });
        }
        return true;
      }),
  });
}

interface AddMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  existingEmails: string[];
}

export function AddMembersDialog({
  open,
  onOpenChange,
  tenantId,
  existingEmails,
}: AddMembersDialogProps) {
  const { t } = useTranslation(["admin"]);
  const { addMembers, isSubmitting } = useAddTenantMembers(tenantId);

  const resolver = useMemo(() => yupResolver(buildSchema(t)), [t]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ emails: string }>({
    defaultValues: { emails: "" },
    resolver,
  });

  useEffect(() => {
    if (!open) {
      reset({ emails: "" });
    }
  }, [open, reset]);

  const showAlreadyMembersWarning = (alreadyMembers: string[]) => {
    if (alreadyMembers.length === 0) return;
    const visible = alreadyMembers.slice(0, MAX_EMAILS_IN_WARNING);
    const overflow = alreadyMembers.length - visible.length;
    const emailsText =
      overflow > 0
        ? `${visible.join(", ")} ${t(
            "admin:tenants.detail.addMembersDialog.andMore",
            { count: overflow },
          )}`
        : visible.join(", ");
    toast.info(
      t("admin:tenants.detail.addMembersDialog.warningAlreadyMembers", {
        emails: emailsText,
      }),
    );
  };

  const onSubmit = async ({ emails }: { emails: string }) => {
    const parsed = splitAndDedupe(emails);
    const existingSet = new Set(
      existingEmails.map((email) => email.toLowerCase()),
    );
    const alreadyInSnapshot = parsed.filter((email) => existingSet.has(email));
    const toAdd = parsed.filter((email) => !existingSet.has(email));

    if (toAdd.length === 0) {
      showAlreadyMembersWarning(alreadyInSnapshot);
      onOpenChange(false);
      return;
    }

    const result = await addMembers(toAdd);

    if (!result.ok) {
      toast.error(t("admin:tenants.detail.addMembersDialog.errorWriteFailed"));
      return;
    }

    const allAlreadyMembers = [...alreadyInSnapshot, ...result.skippedEmails];
    showAlreadyMembersWarning(allAlreadyMembers);

    if (result.addedCount > 0) {
      toast.success(
        t("admin:tenants.detail.addMembersDialog.success", {
          count: result.addedCount,
        }),
      );
    }
    onOpenChange(false);
  };

  const errorMessage = errors.emails?.message;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[28rem] max-w-[90vw]">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogHeader>
            <DialogTitle>
              {t("admin:tenants.detail.addMembersDialog.title")}
            </DialogTitle>
            <DialogDescription>
              {t("admin:tenants.detail.addMembersDialog.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Textarea
              {...register("emails")}
              placeholder={t(
                "admin:tenants.detail.addMembersDialog.placeholder",
              )}
              rows={5}
              disabled={isSubmitting}
              testId="add-members-textarea"
              aria-label={t("admin:tenants.detail.addMembersDialog.title")}
              aria-invalid={errorMessage ? "true" : "false"}
              className={cn(
                errorMessage &&
                  "border-destructive focus:shadow-none focus-visible:ring-destructive",
              )}
            />
            {errorMessage && (
              <p
                className="text-xs italic text-destructive"
                role="alert"
                data-testid="add-members-error"
              >
                {errorMessage}
              </p>
            )}
          </div>

          <DialogFixedFooter>
            <DialogClose asChild>
              <Button
                variant="outline"
                disabled={isSubmitting}
                testId="add-members-cancel"
              >
                {t("admin:tenants.detail.addMembersDialog.cancel")}
              </Button>
            </DialogClose>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
              testId="add-members-submit"
            >
              {isSubmitting
                ? t("admin:tenants.detail.addMembersDialog.submitting")
                : t("admin:tenants.detail.addMembersDialog.submit")}
            </Button>
          </DialogFixedFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
